import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ManufacturingDashboardStats from './components/ManufacturingDashboardStats';
import ManufacturingConfigTab from './components/ManufacturingConfigTab';
import SellerOrderList from '../seller-account-dashboard/components/SellerOrderList';
import SellerPaymentDetails from '../seller-account-dashboard/components/SellerPaymentDetails';
import SellerSettingsTab from '../seller-account-dashboard/components/SellerSettingsTab';

const ManufacturingAccountDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [accountStatus, setAccountStatus] = useState('Active');
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchAccountData = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('manufacturer_accounts')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (data) {
          setAccountStatus(data.account_status);
        } else {
          // If no manufacturer account exists, they shouldn't be here
          // But we'll let ProtectedRoute handle role-based access
        }
      } catch (err) {
        console.error('Error fetching manufacturer status:', err);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchAccountData();
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Activity" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Manufacturing Dashboard</h1>
              <p className="text-muted-foreground">Manage your production orders and configurations.</p>
            </div>
          </div>

          {!statusLoading && accountStatus === 'suspended' && (
            <div className="mb-8 bg-destructive/10 border border-destructive/20 rounded-lg p-6 flex items-start gap-4">
              <div className="w-10 h-10 bg-destructive/20 text-destructive rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-destructive mb-1">Manufacturing Status Suspended</h3>
                <p className="text-destructive/80">
                  Your manufacturing status has been suspended by an administrator. 
                  Please contact support for more information.
                </p>
              </div>
            </div>
          )}

          <div className="mb-8">
            <ManufacturingDashboardStats />
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-6">
            <nav className="-mb-px flex justify-center space-x-8 overflow-x-auto" aria-label="Tabs">
              {['overview', 'orders', 'payments', 'config', 'settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`
                    ${activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize
                  `}
                >
                  {tab === 'config' ? 'Configuration' : tab === 'orders' ? 'Pending Orders' : tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="max-w-7xl mx-auto">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
                    <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                       <Icon name="Clock" size={20} className="text-primary" />
                       Recent Activity
                    </h2>
                    <p className="text-muted-foreground italic">No recent activity found.</p>
                 </div>
                 <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
                    <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                       <Icon name="TrendingUp" size={20} className="text-primary" />
                       Production Metrics
                    </h2>
                    <p className="text-muted-foreground italic">Metrics will appear here as orders are processed.</p>
                 </div>
              </div>
            )}
            {activeTab === 'orders' && (
              <SellerOrderList sellerId={user?.id} statusFilter="pending" isManufacturer={true} />
            )}
            {activeTab === 'payments' && (
              <SellerPaymentDetails sellerId={user?.id} />
            )}
            {activeTab === 'config' && (
              <ManufacturingConfigTab />
            )}
            {activeTab === 'settings' && (
              <SellerSettingsTab isManufacturer={true} />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ManufacturingAccountDashboard;
